import React from 'react';
import Button from '../../../components/ui/Button';

const PlanSelector = ({
  plans,
  selectedPlan,
  onSelectPlan,
  employeeCount,
  onEmployeeCountChange,
  billingCycle,
  onBillingCycleChange
}) => {
  const formatPrice = (plan) => {
    if (!plan) return 'K0';
    
    let price = plan?.monthlyPriceKinaMin || 0;
    
    if (employeeCount > plan?.minEmployees) {
      const priceRange = (plan?.monthlyPriceKinaMax || price) - price;
      const employeeRange = (plan?.maxEmployees || employeeCount) - plan?.minEmployees;
      const pricePerEmployee = priceRange / employeeRange;
      price += pricePerEmployee * (employeeCount - plan?.minEmployees);
    }
    
    return `K${Math.round(price)?.toLocaleString()}`;
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Choose Your Subscription Plan
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Select the plan that best fits your organization's needs
      </p>

      {/* Billing Cycle Toggle */}
      <div className="flex items-center justify-center mb-8">
        <button
          onClick={() => onBillingCycleChange('monthly')}
          className={`px-6 py-2 rounded-l-lg font-medium transition-colors ${
            billingCycle === 'monthly' ?'bg-emerald-600 text-white' :'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300'
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => onBillingCycleChange('annual')}
          className={`px-6 py-2 rounded-r-lg font-medium transition-colors relative ${
            billingCycle === 'annual' ?'bg-emerald-600 text-white' :'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300'
          }`}
        >
          Annual
          <span className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
            Save 15%
          </span>
        </button>
      </div>

      {/* Employee Count Slider */}
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Number of Employees: <span className="text-emerald-600 font-bold">{employeeCount}</span>
        </label>
        <input
          type="range"
          min="1"
          max="500"
          value={employeeCount}
          onChange={(e) => onEmployeeCountChange(parseInt(e?.target?.value))}
          className="w-full h-2 bg-gray-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
        />
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <span>1</span>
          <span>100</span>
          <span>250</span>
          <span>500+</span>
        </div>
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {plans?.map((plan) => {
          const isSelected = selectedPlan?.id === plan?.id;
          const isRecommended = plan?.tierName === 'standard';
          const features = plan?.features || {};
          
          return (
            <div
              key={plan?.id}
              onClick={() => onSelectPlan(plan)}
              className={`relative border-2 rounded-lg p-6 cursor-pointer transition-all ${
                isSelected
                  ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-900/20' :'border-gray-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-700'
              }`}
            >
              {isRecommended && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                    RECOMMENDED
                  </span>
                </div>
              )}
              
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {plan?.displayName}
                </h3>
                <div className="text-3xl font-bold text-emerald-600 mb-1">
                  {formatPrice(plan)}
                  <span className="text-base font-normal text-gray-600 dark:text-gray-400">
                    /{billingCycle === 'monthly' ? 'month' : 'year'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {plan?.minEmployees}-{plan?.maxEmployees || '∞'} employees
                </p>
              </div>

              <div className="space-y-3 mb-6">
                {Object?.entries(features)?.slice(0, 5)?.map(([key, value], idx) => (
                  <div key={idx} className="flex items-start">
                    <svg className="w-5 h-5 text-emerald-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {typeof value === 'boolean' ? key?.replace(/_/g, ' ') : `${key?.replace(/_/g, ' ')}: ${value}`}
                    </span>
                  </div>
                ))}
              </div>

              <Button
                variant={isSelected ? 'default' : 'outline'}
                fullWidth
                onClick={() => onSelectPlan(plan)}
              >
                {isSelected ? 'Selected' : 'Select Plan'}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Enterprise Plan */}
      <div className="mt-6 border-2 border-dashed border-gray-300 dark:border-slate-700 rounded-lg p-6 text-center">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
          Enterprise Plan
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Custom pricing for organizations with 500+ employees
        </p>
        <Button variant="outline" onClick={() => window.location.href = 'mailto:sales@hrpacificrbs.com'}>
          Contact Sales
        </Button>
      </div>
    </div>
  );
};

export default PlanSelector;